import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import GameExpiredRedirect from '@components/GameExpiredRedirect/index';

import { Box, Typography } from '@mui/material';

import { UnityComponent } from '@shared/components';
import { UnityUrlHandler } from '@shared/components/UnityUrlHandler (Safari popup)/UnityUrlHandler';
import { isGameExpired } from '@shared/utils/gameExpiration';
import { getBaseUrl } from '@services/api';
import { addCacheBusting } from '@shared/utils/cache-bust';
import { getUnityLoaderVersion, getLatestFileVersion } from '@shared/utils/file-version';

import CapybaraGame from '../_Games/Capybara';
import FlappyGame from '../_Games/Flappy';
import MinerGameWrapper from '../_Games/Miner/MinerGameWrapper';
import HtmlGameWrapper from '../_Games/HtmlGame/HtmlGameWrapper';
import {
  useFetchGameBuild,
  useFetchGameDefaultsLazy,
} from '../Constructors/ClientMemory/client.actions';

import { useIncreaseGameBuildRunCount } from './game.actions';

import './index.scss';

let isLocalhost = window.location.hostname === 'localhost';

const GamePage = () => {
  const [searchParams] = useSearchParams();
  const [response, increaseGameBuildRunCount, loading] = useIncreaseGameBuildRunCount();
  const [gameUrlPack, setGameUrlPack] = useState<string | null>(null);
  const [webGPUSupported, setWebGPUSupported] = useState<boolean | null>(null);

  const [gameKeyInfo, setGameKeyInfo] = useState<any>(null);
  const [gameKeyLoading, setGameKeyLoading] = useState(true);
  const [fileVersion, setFileVersion] = useState<string | null>(null);

  const currentBuildId = searchParams.get('buildId');


  
 



  // Получаем инфу о ключе по buildId
  useEffect(() => {
    console.log('useEffect for game key info triggered with currentBuildId:', currentBuildId);
    if (currentBuildId && currentBuildId !== 'undefined') {
      console.log('Fetching game key info...');
      setGameKeyLoading(true);
      console.log('Starting fetch request to:', `/api/game-key/by-build/${currentBuildId}`);
      
      // Используем axios с правильным baseURL
      const apiUrl = `/game-key/by-build/${currentBuildId}`;
      console.log('API URL:', apiUrl);
      
      axios.get(apiUrl, {
        baseURL: getBaseUrl(),
        headers: {
          'Content-Type': 'application/json',
        }
      })
        .then(res => {
          console.log('Game key fetch response status:', res.status);
          console.log('Game key fetch response ok:', res.status === 200);
          const data = res.data;
          console.log('Game key data received:', data);
          console.log('Setting gameKeyInfo to:', data.gameKey);
          setGameKeyInfo(data.gameKey);
          console.log('Setting gameKeyLoading to false');
          setGameKeyLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching game key:', error);
          console.log('Setting gameKeyLoading to false due to error');
          setGameKeyLoading(false);
        });
    } else {
      console.log('currentBuildId is null or undefined, skipping game key fetch');
    }
  }, [currentBuildId]);

  // Проверка поддержки WebGPU
  useEffect(() => {
    console.log('Checking WebGPU support...');
    const checkWebGPUSupport = () => {
      try {
        // Простая проверка наличия WebGPU API без вызова requestAdapter
        const hasWebGPU = 'gpu' in navigator && 
                         (navigator as any).gpu && 
                         typeof (navigator as any).gpu.requestAdapter === 'function';
        
        console.log('WebGPU support check result:', hasWebGPU);
        setWebGPUSupported(hasWebGPU);
        
        if (!hasWebGPU) {
          console.warn('WebGPU API not available on this device');
        }
      } catch (error) {
        console.warn('Error checking WebGPU support:', error);
        setWebGPUSupported(false);
      }
    };

    checkWebGPUSupport();
  }, []);

  useEffect(() => {
    console.log('useEffect for increaseGameBuildRunCount triggered with currentBuildId:', currentBuildId);
    if (currentBuildId !== null && currentBuildId !== 'undefined') {
      console.log('Calling increaseGameBuildRunCount...');
      increaseGameBuildRunCount(currentBuildId);
    } else {
      console.log('currentBuildId is null or undefined, skipping increaseGameBuildRunCount');
    }
  }, [currentBuildId]);

  useEffect(() => {
    console.log('useEffect triggered with response:', response);
    if (!response) {
      console.log('no response');
      return;
    }

    const pack = (response as any).gameUrlPack;
    console.log('Setting gameUrlPack to:', pack);
    setGameUrlPack(pack);
    
    console.log('response ', response);
    console.log('gameUrlPack', pack);
  }, [response]);

  // Получаем версию из Unity файлов для cache-busting
  useEffect(() => {
    if (!gameKeyInfo || gameUrlPack === null) return;
    
    const gameKey = gameUrlPack || '001';
    const loaderUrl = `${import.meta.env.VITE_URL}${gameKey}/loader.js`;
    
    // Получаем версию из loader.js или data.br
    const getVersion = async () => {
      try {
        console.log(`🚀 Начало получения версии Unity файлов для cache-busting`);
        console.log(`📦 Game Key: ${gameKey}`);
        console.log(`🔗 Loader URL: ${loaderUrl}`);
        
        // Пытаемся получить версию из loader.js
        const version = await getUnityLoaderVersion(loaderUrl);
        if (version) {
          console.log(`✅ Версия файла успешно получена из loader.js: ${version}`);
          console.log(`📌 Используется версия для cache-busting всех Unity файлов`);
          setFileVersion(version);
        } else {
          console.log(`⚠️ Версия из loader.js не получена, пробуем data.br...`);
          // Fallback: используем версию из data.br
          const dataUrl = `${import.meta.env.VITE_URL}${gameKey}/data.br`;
          const dataVersion = await getLatestFileVersion([dataUrl]);
          if (dataVersion) {
            console.log(`✅ Версия файла получена из data.br: ${dataVersion}`);
            console.log(`📌 Используется версия для cache-busting всех Unity файлов`);
            setFileVersion(dataVersion);
          } else {
            console.warn(`❌ Не удалось получить версию ни из loader.js, ни из data.br`);
            console.warn(`⚠️ Будет использован fallback (buildId или timestamp)`);
          }
        }
      } catch (error) {
        console.error('❌ Ошибка при получении версии файла:', error);
      }
    };
    
    getVersion();
  }, [gameKeyInfo, gameUrlPack]);

  const unityResolution = 1920 / 1080;

  console.log('Component render - gameKeyLoading:', gameKeyLoading, 'gameKeyInfo:', gameKeyInfo);

  // Проверка статуса игры
  if (gameKeyLoading) {
    console.log('Game key loading...');
    // return <div>Loading...</div>;
    return <div></div>;
  }
  

  
  console.log('Game key info:', gameKeyInfo);
  console.log('Loading state:', loading);
  console.log('Game URL pack:', gameUrlPack);
  console.log('WebGPU supported:', webGPUSupported);
  
  if (gameKeyInfo && (!gameKeyInfo.isActive || isGameExpired(gameKeyInfo.activateDate, gameKeyInfo.gameDayCount))) {
    console.log('Game expired or inactive');
    return <GameExpiredRedirect />;
  }

  // Ждём загрузки gameUrlPack и проверки WebGPU
  if (loading || gameUrlPack === null || webGPUSupported === null) {
    console.log('Waiting for data to load...', { loading, gameUrlPack, webGPUSupported });
    return <div></div>;
    // return <div>Loading...</div>;
  }

  // Показываем предупреждение для устройств без поддержки WebGPU
  if (webGPUSupported === false) {
    console.log('WebGPU not supported, showing warning');
    return (
      <Box className="mainPage" sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          Предупреждение о совместимости
        </Typography>
        <Typography sx={{ marginBottom: 3, maxWidth: '500px' }}>
          Ваше устройство может не поддерживать современные графические технологии. 
          Игра может работать медленнее или с ошибками.
        </Typography>
        <button 
          onClick={() => setWebGPUSupported(true)} // Продолжаем загрузку
          style={{
            padding: '12px 24px',
            backgroundColor: '#f3c1c8',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Продолжить загрузку
        </button>
      </Box>
    );
  }

  interface UrlConfig {
    loaderUrl: string;
    dataUrl: string;
    frameworkUrl: string;
    codeUrl: string;
  }
  
  let urlConfig: UrlConfig | Record<string, UrlConfig>;
  
  isLocalhost = false;
  if (isLocalhost) {
    urlConfig = {
      loaderUrl: import.meta.env.VITE_LOADER as string,
      dataUrl: import.meta.env.VITE_DATA as string,
      frameworkUrl: import.meta.env.VITE_FRAMEWORK as string,
      codeUrl: import.meta.env.VITE_CODE as string,
    };
  } else {
    urlConfig = new Proxy<Record<string, UrlConfig>>({}, {
      get(target, key: string) {
        key = String(key); // Ensure key is a string
        if (key === 'default') key = '001'; // Default fallback
  
        // commit
        return {
          loaderUrl: `${import.meta.env.VITE_URL}${key}/loader.js`,
          dataUrl: `${import.meta.env.VITE_URL}${key}/data.br`,
          frameworkUrl: `${import.meta.env.VITE_URL}${key}/framework.js.br`,
          codeUrl: `${import.meta.env.VITE_URL}${key}/wasm.br`,
        };
      },
    });
  }
  

const gameKey = gameUrlPack || '001'; // Если gameUrlPack null, подставляем '001'
  const selectedConfig =
    isLocalhost ? (urlConfig as UrlConfig) : (urlConfig as Record<string, UrlConfig>)[gameKey];

  // Добавляем cache-busting параметры для обхода кэша
  // Приоритет: версия из файла > buildId > timestamp
  const cacheVersion = fileVersion 
    ? fileVersion 
    : currentBuildId && currentBuildId !== 'undefined' 
      ? currentBuildId 
      : gameKeyInfo?.updatedAt 
        ? String(new Date(gameKeyInfo.updatedAt).getTime())
        : String(Date.now());
  
  // Добавляем cache-busting к каждому URL отдельно
  const loaderUrl = addCacheBusting(selectedConfig.loaderUrl, cacheVersion);
  const dataUrl = addCacheBusting(selectedConfig.dataUrl, cacheVersion);
  const frameworkUrl = addCacheBusting(selectedConfig.frameworkUrl, cacheVersion);
  const codeUrl = addCacheBusting(selectedConfig.codeUrl, cacheVersion);

console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
console.log('📦 Unity Cache-Busting Configuration:');
console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
console.log('🔗 Vite URL:', import.meta.env.VITE_URL);
console.log('📦 Game URL Pack:', gameUrlPack);
console.log('🔑 Game Key:', gameKey);
console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
console.log('📌 Итоговая версия для cache-busting:', cacheVersion);
if (fileVersion) {
  console.log('  ✅ Источник: версия из файла (ETag/Last-Modified/хеш)');
} else if (currentBuildId && currentBuildId !== 'undefined') {
  console.log('  ⚠️ Источник: buildId (fallback)');
} else {
  console.log('  ⚠️ Источник: timestamp (fallback)');
}
console.log('🔗 URLs с cache-busting:');
console.log('  - Loader:', loaderUrl);
console.log('  - Data:', dataUrl);
console.log('  - Framework:', frameworkUrl);
console.log('  - Code:', codeUrl);
console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
console.log('Selected Config:', selectedConfig);
console.log('Loader URL:', loaderUrl);
console.log('Data URL:', dataUrl);
console.log('Framework URL:', frameworkUrl);
console.log('Code URL:', codeUrl);

  // Сначала проверяем, является ли это HTML игрой (включая сжатые версии)
  const isHtmlGame = gameKeyInfo?.gameUrlPack?.endsWith('.html') ||
                     gameKeyInfo?.gameUrlPack?.endsWith('.html.br') ||
                     gameKeyInfo?.gameUrlPack?.endsWith('.html.gz');
  
  // Затем проверяем специфические типы игр
  const isMinerGame = gameKeyInfo?.gameType === 'miner';
  const isCapybaraGame = gameKeyInfo?.gameType === 'capybara';
  const isFlappyGame = gameKeyInfo?.gameType === 'flappy';
  
  console.log('Game type check:', {
    gameType: gameKeyInfo?.gameType,
    gameUrlPack,
    isHtmlGame,
    isMinerGame,
    isCapybaraGame,
    isFlappyGame
  });

  // Если это HTML игра - грузим через HTML загрузчик
  if (isHtmlGame) {
    console.log('Loading HTML game from:', gameKeyInfo?.gameUrlPack);
    return (
      <Box className="mainPage" sx={{ width: '100vw', height: '100vh', padding: 0, margin: 0 }}>
        <HtmlGameWrapper gameKey={gameKeyInfo} />
      </Box>
    );
  }

  // Для не-HTML игр используем стандартные загрузчики
  if (isMinerGame) {
    return (
      <Box className="mainPage" sx={{ width: '100vw', height: '100vh', padding: 0, margin: 0 }}>
        <MinerGameWrapper gameKey={gameKeyInfo} gameSettings={null} />
      </Box>
    );
  }

  if (isCapybaraGame) {
    return (
      <Box className="mainPage" sx={{ width: '100vw', height: '100vh', padding: 0, margin: 0 }}>
        <CapybaraGame gameKey={gameKeyInfo} gameSettings={gameKeyInfo?.gameBuild} />
      </Box>
    );
  }

  if (isFlappyGame) {
    return (
      <Box className="mainPage" sx={{ width: '100vw', height: '100vh', padding: 0, margin: 0 }}>
        <FlappyGame gameKey={gameKeyInfo} gameSettings={gameKeyInfo?.gameBuild} />
      </Box>
    );
  }

  // Для всех остальных игр (Unity) показываем Unity компонент
  return (
    <Box className="mainPage">
      <UnityComponent
        loaderUrl={loaderUrl}
        dataUrl={dataUrl}
        frameworkUrl={frameworkUrl}
        codeUrl={codeUrl}
        resolution={unityResolution}
      />
          <UnityUrlHandler /> {/* ✅ Safari popup handler */}
    </Box>
  );
};

export default GamePage;
